/**
 * UpdatePrompt — global, role-agnostic "new version available" banner.
 *
 * Shown to anyone (admin / driver / passenger) once a newer frontend build is
 * detected. Small, fixed, bottom-centre, dismissible — deliberately
 * non-intrusive so it never blocks an active trip. Reload is manual only
 * (never auto), so a driver mid-ride can finish first and reload when ready.
 */
import React, { useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useBuildVersion } from "../hooks/useBuildVersion";

export const UpdatePrompt = () => {
  const { updateAvailable, latestHash, reload } = useBuildVersion();
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md"
      data-testid="update-prompt"
    >
      <div className="bg-[#0A0A0A] text-white border border-white/15 shadow-2xl px-4 py-3 flex items-center gap-3">
        <RefreshCw className="w-4 h-4 flex-shrink-0 text-[#FFC107]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold">New version available</div>
          <div className="text-[11px] text-white/70">
            Reload to update. You can finish what you're doing first.
          </div>
        </div>
        <button
          onClick={async () => { setReloading(true); await reload(); }}
          disabled={reloading}
          className="text-xs font-bold px-3 py-1.5 bg-[#FFC107] text-[#0A0A0A] hover:bg-[#ffce3a] transition-colors disabled:opacity-60 whitespace-nowrap"
          data-testid="update-prompt-reload"
        >
          {reloading ? "Reloading…" : "Reload"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="text-white/50 hover:text-white"
          data-testid="update-prompt-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UpdatePrompt;
